/* @jsx h */
import { useLocalization } from "@lemmonhq/uikit";
import { defineComponent, h } from "vue";
import type { PrinterWifiNetwork } from "./printerWifi";

export type WifiScan =
  | { status: "scanning" }
  | { status: "done"; networks: PrinterWifiNetwork[] }
  | { status: "failed"; detail: string };

const signalIcon = (signal: number) =>
  signal >= -55
    ? "signal_wifi_4_bar"
    : signal >= -67
      ? "network_wifi_3_bar"
      : signal >= -75
        ? "network_wifi_2_bar"
        : "network_wifi_1_bar";

/**
 * The 2.4 GHz networks the phone sees, to pick the printer's network from
 * instead of typing its name.
 */
export const WifiNetworkPicker = defineComponent<{
  scan: WifiScan;
  selectedSsid: string;
  disabled: boolean;
  onSelect: (network: PrinterWifiNetwork) => void;
  onRescan: () => void;
}>({
  name: "PrinterWifiNetworkPicker",
  props: ["scan", "selectedSsid", "disabled", "onSelect", "onRescan"],
  setup(props) {
    const { t } = useLocalization();

    const list = () => {
      const { scan } = props;
      if (scan.status === "scanning") {
        return (
          <div class="flex items-center justify-center gap-2 rounded-xl border border-base-300 px-3 py-4 text-sm text-base-content/60">
            <span class="loading loading-spinner loading-sm" />
            {t("devices.bt_printer.wifi_scanning")}
          </div>
        );
      }
      if (scan.status === "failed") {
        return (
          <div class="rounded-xl border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
            {t("devices.bt_printer.wifi_scan_failed")}
            {scan.detail && (
              <div class="mt-2 break-words font-mono text-xs text-warning/70">
                {scan.detail}
              </div>
            )}
          </div>
        );
      }
      if (scan.networks.length === 0) {
        return (
          <div class="rounded-xl border border-base-300 p-3 text-sm text-base-content/60">
            {t("devices.bt_printer.wifi_no_networks")}
          </div>
        );
      }
      return (
        <div class="max-h-56 space-y-1.5 overflow-y-auto p-px">
          {scan.networks.map((network) => {
            const selected = network.ssid === props.selectedSsid;
            return (
              <button
                key={network.ssid}
                type="button"
                disabled={props.disabled}
                class={[
                  "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left ring-1 transition-all",
                  selected
                    ? "bg-primary/10 ring-primary"
                    : "bg-base-200 ring-base-content/5",
                ]}
                onClick={() => props.onSelect(network)}
              >
                <span
                  class={[
                    "material-symbols-outlined text-lg",
                    selected ? "text-primary" : "text-base-content/60",
                  ]}
                >
                  {signalIcon(network.signal)}
                </span>
                <span class="min-w-0 flex-1 truncate text-sm font-semibold text-accent">
                  {network.ssid}
                </span>
                {selected && (
                  <span class="material-symbols-outlined text-lg text-primary">
                    check_circle
                  </span>
                )}
              </button>
            );
          })}
        </div>
      );
    };

    return () => (
      <div class="form-control w-full">
        <div class="mb-1 flex items-center justify-between">
          <span class="label-text">
            {t("devices.bt_printer.wifi_network_label")}
          </span>
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            disabled={props.disabled || props.scan.status === "scanning"}
            onClick={props.onRescan}
          >
            <span class="material-symbols-outlined text-base">refresh</span>
            {t("devices.bt_printer.wifi_rescan")}
          </button>
        </div>
        {list()}
        <p class="mt-1 text-xs text-base-content/50">
          {t("devices.bt_printer.wifi_band_hint")}
        </p>
      </div>
    );
  },
});
